import { Users } from 'lucide-react';

import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import { parentApi } from '../api/parent.api';
import { ParentResultsReport } from '../components/ParentResultsReport';
import { useParentChild, useSelectedChild } from '../store/child.store';
import { PARENT_PALETTE } from '../theme';

/**
 * Exams & Results, for the parent of the child on screen.
 *
 * Reads `GET /parent/children/:uuid/results`, which is the same server-side
 * read the child's own marks screen is drawn from.
 *
 * Nothing is shown that a teacher did not enter: a remark is the teacher's own
 * words or it is absent, and a paper not yet marked is not a line on the card.
 *
 * The child is never in the address. It comes from the switcher in the sidebar,
 * and the server resolves which children the caller may see from their own
 * token — an id in the URL is an id somebody can change (CLAUDE.md §10).
 */
export function ParentResultsPage() {
  const child = useSelectedChild();
  const isLoadingChildren = useParentChild((state) => state.isLoading);
  const childrenError = useParentChild((state) => state.error);
  const reloadChildren = useParentChild((state) => state.load);

  const { data, isLoading, error, refetch } = useApiQuery(
    () => (child ? parentApi.results(child.uuid) : Promise.resolve(null)),
    [child?.uuid],
  );

  // Switching children refetches; until the new marks land, the old ones belong
  // to somebody else and must not be drawn under this child's name.
  const results = data && data.child.uuid === child?.uuid ? data : null;

  if (isLoadingChildren) {
    return (
      <div className={cn(PARENT_PALETTE, 'flex flex-col gap-6')}>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 rounded-3xl" />
      </div>
    );
  }

  if (childrenError) {
    return (
      <div className={cn(PARENT_PALETTE)}>
        <QueryError
          title="Could not load your children"
          error={new ApiRequestError(childrenError, 0, 'INTERNAL_ERROR')}
          onRetry={() => void reloadChildren()}
        />
      </div>
    );
  }

  if (!child) {
    return (
      <div className={cn(PARENT_PALETTE, 'card')}>
        <EmptyState
          icon={<Users className="h-5 w-5" />}
          title="No child linked to your account yet"
          description="Your school office links a parent's account to their child's record. Once that is done, your child's marks appear here."
        />
      </div>
    );
  }

  return (
    <div className={cn(PARENT_PALETTE)}>
      <ParentResultsReport
        results={results}
        isLoading={isLoading || (!results && !error)}
        error={error}
        onRetry={refetch}
        childName={child.fullName}
        className={child.className}
      />
    </div>
  );
}
